import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Plus } from 'lucide-react';
import { api } from '../api/client';
import type { Employee, TimeOffRequest, TimeOffType } from '@pa/shared';

const TYPES: TimeOffType[] = ['Vacation', 'Sick', 'Child Sick', 'Work from Home', 'Parental Leave'];

export default function TimeOffView() {
  const qc = useQueryClient();
  const requests = useQuery({
    queryKey: ['timeoff'],
    queryFn: () => api.get<TimeOffRequest[]>('/api/timeoff'),
  });
  const employees = useQuery({
    queryKey: ['employees'],
    queryFn: () => api.get<Employee[]>('/api/employees'),
  });
  const [showForm, setShowForm] = useState(false);

  const create = useMutation({
    mutationFn: (d: Partial<TimeOffRequest>) => api.post<TimeOffRequest>('/api/timeoff', d),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['timeoff'] });
      setShowForm(false);
    },
  });
  const approve = useMutation({
    mutationFn: (id: string) => api.post<TimeOffRequest>(`/api/timeoff/${id}/approve`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['timeoff'] }),
  });
  const reject = useMutation({
    mutationFn: (id: string) => api.post<TimeOffRequest>(`/api/timeoff/${id}/reject`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['timeoff'] }),
  });

  return (
    <div className="max-w-6xl">
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-3xl font-bold">Time off</h1>
        <button
          onClick={() => setShowForm((v) => !v)}
          className="inline-flex items-center gap-2 bg-[#6344C6] hover:bg-[#5a3eb8] text-white rounded-xl px-4 py-2.5 text-sm font-medium"
          disabled={!employees.data || employees.data.length === 0}
        >
          <Plus size={16} /> New request
        </button>
      </div>

      {showForm && employees.data && (
        <RequestForm
          employees={employees.data}
          onSubmit={(d) => create.mutate(d)}
          submitting={create.isPending}
        />
      )}

      {requests.data && requests.data.length === 0 && (
        <div className="bg-white border border-dashed border-slate-200 rounded-2xl p-12 text-center text-slate-500">
          <p className="font-medium">No time-off requests yet</p>
        </div>
      )}

      {requests.data && requests.data.length > 0 && (
        <div className="bg-white border border-slate-100 rounded-2xl overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-slate-50">
              <tr className="text-left text-slate-500 text-xs uppercase tracking-widest">
                <th className="px-6 py-3 font-semibold">Employee</th>
                <th className="px-6 py-3 font-semibold">Type</th>
                <th className="px-6 py-3 font-semibold">Dates</th>
                <th className="px-6 py-3 font-semibold">Status</th>
                <th className="px-6 py-3 font-semibold" />
              </tr>
            </thead>
            <tbody>
              {requests.data.map((r) => {
                const emp = employees.data?.find((e) => e.id === r.employeeId);
                return (
                  <tr key={r.id} className="border-t border-slate-100">
                    <td className="px-6 py-4 font-medium">{emp?.name ?? r.employeeId}</td>
                    <td className="px-6 py-4 text-slate-600">{r.type}</td>
                    <td className="px-6 py-4 text-slate-600">
                      {new Date(r.startDate).toLocaleDateString()} – {new Date(r.endDate).toLocaleDateString()}
                    </td>
                    <td className="px-6 py-4">
                      <span className={`text-xs font-semibold uppercase tracking-wider ${statusColor(r.status)}`}>
                        {r.status}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-right">
                      {r.status === 'Pending' && (
                        <div className="flex justify-end gap-2">
                          <button onClick={() => approve.mutate(r.id)} className="text-xs font-medium text-emerald-600 hover:text-emerald-700">Approve</button>
                          <button onClick={() => reject.mutate(r.id)} className="text-xs font-medium text-rose-600 hover:text-rose-700">Reject</button>
                        </div>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function statusColor(status: string) {
  if (status === 'Approved') return 'text-emerald-600';
  if (status === 'Rejected') return 'text-rose-600';
  return 'text-amber-600';
}

function RequestForm({
  employees,
  onSubmit,
  submitting,
}: {
  employees: Employee[];
  onSubmit: (d: Partial<TimeOffRequest>) => void;
  submitting: boolean;
}) {
  const [data, setData] = useState({
    employeeId: employees[0]?.id ?? '',
    type: 'Vacation' as TimeOffType,
    startDate: new Date().toISOString().split('T')[0],
    endDate: new Date().toISOString().split('T')[0],
    reason: '',
  });
  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        onSubmit(data);
      }}
      className="bg-white border border-slate-100 rounded-2xl p-6 mb-6 grid grid-cols-2 gap-4"
    >
      <Field label="Employee">
        <select required value={data.employeeId} onChange={(e) => setData({ ...data, employeeId: e.target.value })} className={inputCls}>
          {employees.map((e) => <option key={e.id} value={e.id}>{e.name}</option>)}
        </select>
      </Field>
      <Field label="Type">
        <select value={data.type} onChange={(e) => setData({ ...data, type: e.target.value as TimeOffType })} className={inputCls}>
          {TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
        </select>
      </Field>
      <Field label="From"><input required type="date" value={data.startDate} onChange={(e) => setData({ ...data, startDate: e.target.value })} className={inputCls} /></Field>
      <Field label="To"><input required type="date" value={data.endDate} onChange={(e) => setData({ ...data, endDate: e.target.value })} className={inputCls} /></Field>
      <Field label="Reason (optional)">
        <input value={data.reason} onChange={(e) => setData({ ...data, reason: e.target.value })} className={inputCls} />
      </Field>
      <div className="col-span-2 flex justify-end">
        <button disabled={submitting} className="bg-[#6344C6] hover:bg-[#5a3eb8] disabled:opacity-50 text-white rounded-xl px-4 py-2.5 text-sm font-medium">
          {submitting ? 'Saving…' : 'Submit'}
        </button>
      </div>
    </form>
  );
}

const inputCls = 'w-full border border-slate-200 rounded-xl px-3 py-2 focus:outline-none focus:ring-2 focus:ring-[#6344C6]/40';

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <label className="text-xs font-semibold uppercase tracking-widest text-slate-500 mb-1 block">{label}</label>
      {children}
    </div>
  );
}
